import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  UploadCloud,
  FileSpreadsheet,
  MoveRightIcon,
  Loader2,
  CheckCircle2,
  Info,
  XCircle
} from "lucide-react";
import Papa from "papaparse";
import { addPartsBulk } from "../store/useParts/usePartsThunk";
import { getSellerDashboardStatsByUserId } from "../store/dashboard/dashboardActions";
import { usePartsQuota } from "../hooks/usePartsQuota";
import PaymentGenerateModal from "./Modal/PaymentGenerateModal";
import { CsvPreviewTable } from "./bulk-part/CsvPreviewTable";
import {
  validateRowData,
  ValidationErrors,
  ValidationSuccess
} from "./bulk-part/Validations";
import { PaymentLinkErrors } from "./bulk-part/PaymentLinkErrors";
import { ColumnMappingInfo } from "./bulk-part/ColumnMappingInfo";
import { separateRowsByPaymentLink } from "./bulk-part/SeparateRowByPaymentLink";
import {
  mapColumnsToCanonical,
  transformRowWithMapping
} from "./bulk-part/transformRowWithMapping";
import { CANONICAL_COLUMNS } from "./bulk-part/Columns";
import {
  fetchCategoriesAndMakes,
  fetchModelsByMake,
  fetchPartNamesByCategoryId
} from "../store/parts/partsAction";

const TEMPLATE_FILE_PATH = "/templates/parts_upload_template.csv";

const BulkAddPartsForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canAddParts, partsLimit, totalParts, remaining } = usePartsQuota();
  const { user, currentSubscription } = useSelector((state) => state.auth);
  const { bulkLoading: loading } = useSelector((state) => state.useParts);
  const [open, setOpen] = useState(false);
  const { categories, makes } = useSelector((state) => state.parts);
  const currentPlan = currentSubscription?.previousPlanId
    ? currentSubscription?.previousPlanId
    : currentSubscription?.planId;
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [processedCsvData, setProcessedCsvData] = useState([]);
  const [paymentLinkErrors, setPaymentLinkErrors] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [unmappedColumns, setUnmappedColumns] = useState([]);
  const [missingRequiredColumns, setMissingRequiredColumns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [modelsCache, setModelsCache] = useState({});
  const [partNamesCache, setPartNamesCache] = useState({});
  const [uploadSummary, setUploadSummary] = useState({
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    paymentLinkErrors: 0
  });

  const getMakeIdByName = useCallback(
    (makeName) => {
      const make = makes?.find((m) => m.name === makeName);
      return make?.id || null;
    },
    [makes]
  );

  const getCategoryIdByName = useCallback(
    (categoryName) => {
      const category = categories?.find((cat) => cat.name === categoryName);
      return category?.id || null;
    },
    [categories]
  );

  const fetchModelsForMake = useCallback(
    async (makeId, makeName) => {
      if (!makeId) return [];

      if (modelsCache[makeId]) {
        return modelsCache[makeId];
      }

      try {
        const result = await dispatch(fetchModelsByMake(makeId)).unwrap();
        setModelsCache((prev) => ({ ...prev, [makeId]: result }));
        return result;
      } catch (error) {
        console.error(`Error fetching models for ${makeName}:`, error);
        return [];
      }
    },
    [dispatch, modelsCache]
  );

  const fetchPartNamesForCategory = useCallback(
    async (categoryId, categoryName) => {
      if (!categoryId) return [];

      const isOtherCategory =
        categoryName?.toLowerCase() === "other" ||
        categoryName?.toLowerCase() === "others";

      if (isOtherCategory) return [];

      if (partNamesCache[categoryId]) {
        return partNamesCache[categoryId];
      }

      try {
        const result = await dispatch(
          fetchPartNamesByCategoryId(categoryId)
        ).unwrap();
        setPartNamesCache((prev) => ({ ...prev, [categoryId]: result }));
        return result;
      } catch (error) {
        console.error(`Error fetching part names for ${categoryName}:`, error);
        return [];
      }
    },
    [dispatch, partNamesCache]
  );

  const valueExistsInArray = useCallback((value, array, key = "name") => {
    if (!value || !Array.isArray(array)) return false;
    return array.some(
      (item) => item[key]?.toLowerCase() === value?.toLowerCase()
    );
  }, []);

  const processCsvData = useCallback(
    async (data) => {
      const processedRows = [];
      const totalRows = data?.length;

      for (let i = 0; i < data?.length; i++) {
        const row = data[i];
        const processedRow = { ...row };

        setProcessingProgress(Math.round(((i + 1) / totalRows) * 100));
        setProcessingStage(`Processing row ${i + 1} of ${totalRows}...`);

        const makeExists = valueExistsInArray(
          row[CANONICAL_COLUMNS.MAKE],
          makes
        );

        if (!makeExists) {
          processedRow.make = "Other";
          processedRow.makeOther = row[CANONICAL_COLUMNS.MAKE];
          processedRow.makeId = "";
        } else {
          processedRow.make = row[CANONICAL_COLUMNS.MAKE];
          processedRow.makeOther = "";
          processedRow.makeId = getMakeIdByName(row[CANONICAL_COLUMNS.MAKE]);
        }

        if (processedRow.makeId) {
          const models = await fetchModelsForMake(
            processedRow.makeId,
            processedRow.make
          );

          const modelExists = valueExistsInArray(
            row[CANONICAL_COLUMNS.MODEL],
            models
          );

          if (!modelExists) {
            processedRow.model = "Other";
            processedRow.modelOther = row[CANONICAL_COLUMNS.MODEL];
          } else {
            processedRow.model = row[CANONICAL_COLUMNS.MODEL];
            processedRow.modelOther = "";
          }
        } else {
          processedRow.model = "Other";
          processedRow.modelOther = row[CANONICAL_COLUMNS.MODEL];
        }

        const categoryExists = valueExistsInArray(
          row[CANONICAL_COLUMNS.PART_CATEGORY],
          categories
        );

        if (!categoryExists) {
          processedRow.partCategory = "Others";
          processedRow.partCategoryOther = row[CANONICAL_COLUMNS.PART_CATEGORY];
          processedRow.partCategoryId = getCategoryIdByName("Others") || "";
        } else {
          processedRow.partCategory = row[CANONICAL_COLUMNS.PART_CATEGORY];
          processedRow.partCategoryOther = "";
          processedRow.partCategoryId = getCategoryIdByName(
            row[CANONICAL_COLUMNS.PART_CATEGORY]
          );
        }

        const isOtherCategory =
          processedRow.partCategory?.toLowerCase() === "other" ||
          processedRow.partCategory?.toLowerCase() === "others";

        if (processedRow.partCategoryId && !isOtherCategory) {
          const partNames = await fetchPartNamesForCategory(
            processedRow.partCategoryId,
            processedRow.partCategory
          );

          const partNameExists = valueExistsInArray(
            row[CANONICAL_COLUMNS.PART_NAME],
            partNames
          );

          if (!partNameExists) {
            processedRow.partName = "Other";
            processedRow.partNameOther = row[CANONICAL_COLUMNS.PART_NAME];
          } else {
            processedRow.partName = row[CANONICAL_COLUMNS.PART_NAME];
            processedRow.partNameOther = "";
          }
        } else {
          processedRow.partName = "Other";
          processedRow.partNameOther = row[CANONICAL_COLUMNS.PART_NAME];
        }

        processedRow.year = row[CANONICAL_COLUMNS.YEAR];
        processedRow.price = row[CANONICAL_COLUMNS.PRICE];
        processedRow.quantity = row[CANONICAL_COLUMNS.QUANTITY];
        processedRow.postalCode = row[CANONICAL_COLUMNS.POSTAL_CODE];
        processedRow.detailedDescription =
          row[CANONICAL_COLUMNS.DESCRIPTION] || "";
        processedRow.stripePaymentLink =
          row[CANONICAL_COLUMNS.STRIPE_LINK] || "";

        processedRows.push(processedRow);
      }

      return processedRows;
    },
    [
      categories,
      makes,
      valueExistsInArray,
      getMakeIdByName,
      getCategoryIdByName,
      fetchModelsForMake,
      fetchPartNamesForCategory
    ]
  );

  const validateCsvData = useCallback((data) => {
    const errors = [];
    const validRows = [];
    const invalidRows = [];

    if (!data || data?.length === 0) {
      errors.push("CSV file is empty");
      return { errors, validRows, invalidRows };
    }

    data.forEach((row, index) => {
      const rowErrors = validateRowData(row);
      const rowNumber = index + 2;

      if (rowErrors?.length > 0) {
        errors.push({
          rowNumber,
          errors: rowErrors,
          rowData: row
        });
        invalidRows.push({
          ...row,
          rowNumber,
          hasErrors: true,
          errorMessages: rowErrors
        });
      } else {
        validRows.push({
          ...row,
          rowNumber,
          hasErrors: false
        });
      }
    });

    return { errors, validRows, invalidRows };
  }, []);

  const resetStates = useCallback(() => {
    setSelectedFile(null);
    setCsvData([]);
    setValidationErrors([]);
    setPreviewData([]);
    setProcessedCsvData([]);
    setPaymentLinkErrors([]);
    setColumnMapping({});
    setUnmappedColumns([]);
    setMissingRequiredColumns([]);
    setModelsCache({});
    setPartNamesCache({});
    setIsProcessing(false);
    setProcessingStage("");
    setProcessingProgress(0);
    setUploadSummary({
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      paymentLinkErrors: 0
    });
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      resetStates();
      return;
    }

    setPreviewData([]);
    setProcessedCsvData([]);

    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      toast({
        variant: "destructive",
        title: "Invalid File Type",
        description: "Please upload a CSV file."
      });
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: "Please upload a file smaller than 5MB."
      });
      e.target.value = null;
      return;
    }

    setSelectedFile(file);
    setIsProcessing(true);
    setProcessingStage("Parsing CSV file...");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      transformHeader: (header) => (header ? header.trim() : header),
      transform: (value) => (typeof value === "string" ? value.trim() : value),
      complete: async ({ data, errors }) => {
        if (errors && errors.length > 0) {
          toast({
            variant: "destructive",
            title: "CSV Parse Error",
            description:
              errors[0].message || "There was an error parsing the CSV file."
          });
          setSelectedFile(null);
          setIsProcessing(false);
          return;
        }

        const cleanedData = data.filter((row) =>
          Object.values(row).some((v) => v !== "")
        );

        if (cleanedData.length === 0) {
          toast({
            variant: "destructive",
            title: "Empty File",
            description: "The CSV file contains no data rows."
          });
          setSelectedFile(null);
          setIsProcessing(false);
          return;
        }

        setProcessingStage("Mapping columns...");

        const headers = Object.keys(cleanedData[0]);
        const {
          mapping,
          unmappedColumns: unmapped,
          missingRequiredColumns: missingRequired
        } = mapColumnsToCanonical(headers);

        setColumnMapping(mapping);
        setUnmappedColumns(unmapped);
        setMissingRequiredColumns(missingRequired || []);
        if (missingRequired && missingRequired.length > 0) {
          toast({
            variant: "destructive",
            title: "Missing Required Columns",
            description: `The following required columns are missing: ${missingRequired.join(
              ", "
            )}. Please check your CSV file format and download the template for reference.`
          });
          setSelectedFile(null);
          setIsProcessing(false);
          return;
        }

        if (unmapped.length > 0) {
          toast({
            variant: "default",
            title: "Unrecognized Columns",
            description: `The following columns were not recognized and will be ignored: ${unmapped.join(
              ", "
            )}`
          });
        }

        setProcessingStage("Transforming data...");

        const transformedData = cleanedData.map((row) =>
          transformRowWithMapping(row, mapping)
        );

        setProcessingStage("Validating payment links...");

        const { validRows: paymentValidRows, invalidRows: paymentInvalidRows } =
          separateRowsByPaymentLink(transformedData);
        setPaymentLinkErrors(paymentInvalidRows);

        const dataToProcess = paymentValidRows;

        const newTotal = totalParts + dataToProcess.length;
        if (
          !canAddParts ||
          (partsLimit !== "unlimited" && newTotal > partsLimit)
        ) {
          toast({
            variant: "destructive",
            title: "Plan Limit Reached",
            description: `Your plan allows up to ${partsLimit} parts. You have ${totalParts}, tried adding ${dataToProcess.length}. Only ${remaining} more allowed.`
          });
          setSelectedFile(null);
          setIsProcessing(false);
          return;
        }

        setProcessingStage("Validating data...");

        const {
          errors: validationErrorsList,
          validRows,
          invalidRows
        } = validateCsvData(dataToProcess);

        setValidationErrors(validationErrorsList);

        setUploadSummary({
          totalRows: cleanedData.length,
          validRows: validRows.length,
          invalidRows: invalidRows.length,
          paymentLinkErrors: paymentInvalidRows.length
        });

        setPreviewData(validRows);

        setCsvData(validRows);

        if (validRows.length === 0) {
          toast({
            variant: "destructive",
            title: "No Valid Data",
            description:
              "All rows contain validation errors. Please fix them and try again."
          });
          setIsProcessing(false);
          return;
        }

        setProcessingStage("Matching with database...");

        const processedData = await processCsvData(validRows);
        setProcessedCsvData(processedData);

        setIsProcessing(false);
        setProcessingStage("");
        setProcessingProgress(0);

        if (paymentInvalidRows.length > 0 || invalidRows.length > 0) {
          toast({
            title: "File Processed with Issues",
            description: `${validRows.length} valid rows ready. ${
              invalidRows.length
            } validation errors${
              paymentInvalidRows.length > 0
                ? ` and ${paymentInvalidRows.length} payment link errors`
                : ""
            }.`
          });
        } else {
          toast({
            title: "File Validated Successfully",
            description: `${validRows.length} parts ready for upload.`
          });
        }
      },
      error: (error) => {
        toast({
          variant: "destructive",
          title: "File Parse Error",
          description: error.message
        });
        setSelectedFile(null);
        setIsProcessing(false);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast({
        variant: "destructive",
        title: "No File Selected",
        description: "Please choose a CSV file to upload."
      });
      return;
    }

    if (!user) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "You must be logged in to upload parts."
      });
      return;
    }

    if (csvData?.length === 0) {
      toast({
        variant: "destructive",
        title: "No Valid Data",
        description: "The CSV file contains no valid data rows."
      });
      return;
    }

    try {
      const result = await dispatch(
        addPartsBulk({
          currentPlan,
          user,
          csvData: processedCsvData,
          file: selectedFile,
          categories
        })
      ).unwrap();

      let successMessage = `Successfully added ${result.count} parts to your inventory.`;
      if (paymentLinkErrors?.length > 0) {
        successMessage += ` ${paymentLinkErrors?.length} part(s) were skipped due to invalid payment links.`;
      }

      toast({
        title: "Upload Successful",
        description: successMessage
      });

      resetStates();
      navigate("/dashboard");

      const fileInput = document.getElementById("bulkPartsFile");
      if (fileInput) fileInput.value = null;
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error || "There was an error processing your file."
      });
    }
  };

  useEffect(() => {
    if (user?.uid && user?.role === "seller") {
      dispatch(getSellerDashboardStatsByUserId(user.uid));
    }
    dispatch(fetchCategoriesAndMakes());
  }, [dispatch, user?.uid, user?.role]);

  const isFormValid =
    selectedFile &&
    !loading &&
    !isProcessing &&
    csvData?.length > 0 &&
    processedCsvData?.length > 0 &&
    missingRequiredColumns?.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.1 }}
    >
      <Card className="border shadow-md">
        <CardHeader>
          <CardTitle className="text-xl md:text-2xl">Upload CSV File</CardTitle>
          <CardDescription>
            Upload a CSV file containing your parts inventory. Ensure the file
            follows the required format.
            <a
              href={TEMPLATE_FILE_PATH}
              download="parts_upload_template.csv"
              className="text-primary underline ml-1 hover:text-primary/80"
            >
              Download Template
            </a>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => setOpen(true)}
                className="group flex items-center text-sm font-medium text-primary hover:text-accent transition-colors duration-200"
              >
                How to Generate a Stripe Payment Link
                <MoveRightIcon className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="bulkPartsFile">CSV File</Label>
              <div className="flex items-center justify-center w-full">
                <Label
                  htmlFor="bulkPartsFile"
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer bg-secondary/50 hover:bg-secondary/70 border-primary/30 hover:border-primary/50 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-10 h-10 mb-3 text-primary animate-spin" />
                        <p className="mb-2 text-sm font-semibold text-foreground">
                          {processingStage}
                        </p>
                        {processingProgress > 0 && (
                          <div className="w-48 bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${processingProgress}%` }}
                            />
                          </div>
                        )}
                      </>
                    ) : selectedFile ? (
                      <>
                        <CheckCircle2 className="w-10 h-10 mb-3 text-green-600" />
                        <p className="mb-2 text-sm font-semibold text-foreground">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {csvData?.length > 0
                            ? `${csvData?.length} valid rows ready`
                            : "Processed"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click or drag file to replace
                        </p>
                      </>
                    ) : (
                      <>
                        <UploadCloud className="w-10 h-10 mb-3 text-primary" />
                        <p className="mb-2 text-sm text-muted-foreground">
                          <span className="font-semibold">Click to upload</span>{" "}
                          or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground">
                          CSV file (MAX. 5MB)
                        </p>
                      </>
                    )}
                  </div>
                  <Input
                    id="bulkPartsFile"
                    type="file"
                    accept=".csv, text/csv"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={loading || isProcessing}
                  />
                </Label>
              </div>
            </div>

            {/* Missing Required Columns Error */}
            {missingRequiredColumns?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-50 rounded-lg border border-red-300"
              >
                <div className="flex items-center mb-3">
                  <XCircle className="w-5 h-5 text-red-600 mr-2" />
                  <h3 className="font-semibold text-red-800">
                    Missing Required Columns
                  </h3>
                </div>
                <p className="text-sm text-red-700 mb-2">
                  The following required columns are missing from your CSV file:
                </p>
                <ul className="list-disc list-inside text-red-700 mb-3">
                  {missingRequiredColumns?.map((column) => (
                    <li key={column} className="font-mono">
                      {column}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-red-700">
                  Please download the template and ensure all required columns
                  are present.
                </p>
              </motion.div>
            )}

            {/* Upload Summary */}
            {selectedFile && !isProcessing && uploadSummary.totalRows > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-blue-50 rounded-lg border border-blue-200"
              >
                <div className="flex items-center mb-3">
                  <Info className="w-5 h-5 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-blue-800">
                    Upload Summary
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {uploadSummary.totalRows}
                    </div>
                    <div className="text-blue-700">Total Rows</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {uploadSummary.validRows}
                    </div>
                    <div className="text-green-700">Valid Rows</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {uploadSummary.invalidRows}
                    </div>
                    <div className="text-red-700">Validation Errors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {uploadSummary.paymentLinkErrors}
                    </div>
                    <div className="text-yellow-700">Payment Errors</div>
                  </div>
                </div>
                <div className="mt-3 text-sm text-blue-700">
                  <p>
                    <strong>Note:</strong> Only {uploadSummary.validRows} valid
                    rows will be uploaded.
                    {uploadSummary.invalidRows > 0 &&
                      ` ${uploadSummary.invalidRows} rows with validation errors will be skipped.`}
                    {uploadSummary.paymentLinkErrors > 0 &&
                      ` ${uploadSummary.paymentLinkErrors} rows with payment link errors will be skipped.`}
                  </p>
                </div>
              </motion.div>
            )}

            <AnimatePresence mode="wait">
              {/* Column Mapping Info */}
              {selectedFile &&
                !isProcessing &&
                Object.keys(columnMapping)?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <ColumnMappingInfo
                      mapping={columnMapping}
                      unmappedColumns={unmappedColumns}
                    />
                  </motion.div>
                )}

              {/* Payment Link Errors */}
              {selectedFile &&
                !isProcessing &&
                paymentLinkErrors?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <PaymentLinkErrors invalidRows={paymentLinkErrors} />
                  </motion.div>
                )}

              {/* Validation Errors */}
              {selectedFile &&
                !isProcessing &&
                validationErrors?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <ValidationErrors errors={validationErrors} />
                  </motion.div>
                )}

              {/* Success Message */}
              {selectedFile && !isProcessing && csvData?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <ValidationSuccess count={csvData?.length} />
                </motion.div>
              )}

              {/* Preview Table - Only Valid Rows */}
              {previewData?.length > 0 &&
                !isProcessing &&
                processedCsvData?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <CsvPreviewTable
                      previewData={previewData}
                      processedCsvData={processedCsvData}
                    />
                  </motion.div>
                )}
            </AnimatePresence>

            {/* Format Guide */}
            {(!selectedFile || validationErrors?.length > 0) &&
              !isProcessing && (
                <div className="text-sm p-4 bg-accent/50 rounded-md border border-accent">
                  <p className="font-semibold mb-2">📋 Required CSV Columns:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>
                      <code>Year</code> (e.g., 2018) - Must be between 1900 and{" "}
                      {new Date().getFullYear() + 1}
                    </li>
                    <li>
                      <code>Make</code> (e.g., Honda, Toyota, Ford) - Will be
                      matched against your database
                    </li>
                    <li>
                      <code>Model</code> (e.g., Civic, Camry, F-150) - Will be
                      matched against your database
                    </li>
                    <li>
                      <code>PartName</code> (e.g., Engine, Transmission,
                      Headlight) - Will be matched against your database
                    </li>
                    <li>
                      <code>PartCategory</code> (e.g., Engine, Brakes,
                      Electrical) - Will be matched against your database
                    </li>
                    <li>
                      <code>Price</code> (e.g., 99.99 - numbers only, no
                      currency symbols)
                    </li>
                    <li>
                      <code>Quantity</code> (e.g., 1, 5, 10 - whole numbers
                      only)
                    </li>
                    <li>
                      <code>PostalCode</code> (e.g., A1B 2C3, 12345)
                    </li>
                    <li>
                      <code>DetailedDescription</code> (Optional - any text)
                    </li>
                    <li>
                      <code>StripePaymentLink</code> (Optional - e.g.,
                      https://buy.stripe.com/...)
                    </li>
                  </ul>

                  <p className="text-xs text-muted-foreground mt-3">
                    ⚠️ <strong>Important:</strong> Values will be matched
                    against your database. Unmatched values will automatically
                    be set as "Other" with the original value preserved.
                  </p>
                </div>
              )}

            {/* Upload Button */}
            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                size="lg"
                disabled={!isFormValid}
                className="w-full px-2 xs:w-auto xs:px-auto relative"
              >
                {loading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      className="mr-2"
                    >
                      <FileSpreadsheet className="h-5 w-5" />
                    </motion.div>
                    Uploading {csvData?.length} parts...
                  </>
                ) : (
                  <>
                    <UploadCloud className="mr-2 h-5 w-5" />
                    Upload {csvData?.length} Valid Parts
                    {uploadSummary.totalRows > 0 && (
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
                        {uploadSummary.validRows}
                      </span>
                    )}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <PaymentGenerateModal open={open} setOpen={setOpen} isBulk={true} />
    </motion.div>
  );
};

export default BulkAddPartsForm;
